import db from "../db/database";

// Clave usada para almacenar la información de caja activa en localStorage como respaldo
const ACTIVE_CAJA_KEY = "exceliaone_caja_activa";

/**
 * Obtiene la información de la apertura de caja actualmente activa
 * @returns {Promise<Object|null>} Información de la apertura activa o null si no hay ninguna
 */
export const getAperturaActiva = async () => {
    try {
        // Intentar obtener apertura activa desde localStorage (respaldo)
        const localData = localStorage.getItem(ACTIVE_CAJA_KEY);
        let aperturaActivaId = localData ? JSON.parse(localData).id : null;

        // Buscar aperturas con estado "Abierto"
        const aperturasAbiertas = await db.aperturaDeCaja
            .filter(apertura => apertura.Estado === "Abierto" && !apertura.deleted)
            .toArray();

        // Si hay aperturas abiertas, tomar la primera (la más reciente)
        if (aperturasAbiertas.length > 0) {
            // Si hay alguna apertura abierta, actualizamos el localStorage
            const aperturaActiva = aperturasAbiertas[0];
            await setAperturaActiva(aperturaActiva);
            return aperturaActiva;
        }

        // Si no hay aperturas abiertas pero hay ID en localStorage
        if (aperturaActivaId) {
            const apertura = await db.aperturaDeCaja.get(aperturaActivaId);
            // Si la apertura existe pero no está abierta, limpiar el estado
            if (!apertura || apertura.Estado !== "Abierto" || apertura.deleted) {
                await clearAperturaActiva();
                return null;
            }
            return apertura;
        }

        return null;
    } catch (error) {
        console.error("Error al obtener apertura activa:", error);
        return null;
    }
};

/**
 * Establece una apertura como la caja actualmente activa
 * @param {Object} apertura - Objeto con la información de la apertura
 * @returns {Promise<boolean>} True si se estableció correctamente
 */
export const setAperturaActiva = async (apertura) => {
    try {
        if (!apertura || !apertura.id) {
            throw new Error("Apertura inválida");
        }

        // Guardar en localStorage como respaldo
        localStorage.setItem(ACTIVE_CAJA_KEY, JSON.stringify({
            id: apertura.id,
            No: apertura.No,
            Cajero: apertura.Cajero,
            Caja: apertura.Caja,
            Turno: apertura.Turno,
            timestamp: new Date().toISOString()
        }));

        return true;
    } catch (error) {
        console.error("Error al establecer apertura activa:", error);
        return false;
    }
};

/**
 * Limpia la información de caja activa
 * @returns {Promise<boolean>} True si se limpió correctamente
 */
export const clearAperturaActiva = async () => {
    try {
        localStorage.removeItem(ACTIVE_CAJA_KEY);
        return true;
    } catch (error) {
        console.error("Error al limpiar apertura activa:", error);
        return false;
    }
};

/**
 * Verifica si hay una caja abierta actualmente
 * @returns {Promise<boolean>} True si hay una caja abierta
 */
export const hayCajaAbierta = async () => {
    const aperturaActiva = await getAperturaActiva();
    return !!aperturaActiva;
};

/**
 * Obtiene la información básica de la caja activa para mostrar en UI
 * @returns {Promise<Object>} Objeto con información de la caja activa
 */
export const getInfoCajaActiva = async () => {
    const apertura = await getAperturaActiva();
    if (!apertura) return null;

    return {
        id: apertura.id,
        No: apertura.No,
        Cajero: apertura.Cajero,
        Caja: apertura.Caja,
        Turno: apertura.Turno,
        FechaDeApertura: apertura.FechaDeApertura,
        HoraDeApertura: apertura.HoraDeApertura
    };
}; 